export type TemplateColor = "black" | "white" | "red"

export type HexColor = `#${string}`
export type ColorValue = HexColor | "transparent"
export type BadgeFontWeight = 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900

export enum PrinterModel {
  QL_810_820_SERIES = "QL_810_820_SERIES",
  GENERIC = "GENERIC",
}

export enum PrinterMedia {
  DK_2205 = "DK-2205",
  DK_2251 = "DK-2251",
  GENERIC = "GENERIC",
  AVERY_5392 = "AVERY_5392",
  AVERY_5390 = "AVERY_5390",
  AVERY_8395 = "AVERY_8395",
}

export enum BadgeElementType {
  TEXT = "text",
  FIELD = "field",
  IMAGE = "image",
  LINE = "line",
  RECTANGLE = "rectangle",
  OVAL = "oval",
  QR_CODE = "qr_code",
}

export enum OutputUnits {
  PIXELS = "pixels",
  MM = "mm",
  INCHES = "inches",
}

export type BorderStyle = {
  color: ColorValue
  width: number // mm
  style: "solid" | "dashed" | "dotted"
}

export type TransformStyle = {
  rotate: number // degrees
  flipHorizontal: boolean
  flipVertical: boolean
}

export type PrintOrientation = "portrait" | "landscape"

export type BadgeTemplate = {
  id: string
  name: string
  eventId?: string
  widthMm: number
  heightMm: number
  printerOrientation: PrintOrientation
  printerModel: PrinterModel
  printerMedia: PrinterMedia
  badgeData: {
    elements: BadgeElement[]
  }
  html: string
  manifest: BadgeTemplateManifest
  createdAt: string
  updatedAt: string
}

export type BadgeTemplateManifest = {
  images: Array<{
    id: string
    url: string
    widthPx: number
    heightPx: number
    fit: "crop" | "clip" | "scale" // imgix-specific
  }>
  qrCodes: Array<{
    id: string
    format: BadgeQRFormat
    widthPx: number
    heightPx: number
  }>
}

// Base properties shared by all badge elements
type BadgeElementBase = {
  id: string
  // Positioning from top-left
  x: number
  y: number
  // Dimensions in mm
  width: number
  height: number
  zIndex: number
  // Common styling
  fill?: ColorValue // background color hex or transparent
  border?: BorderStyle
  transforms?: TransformStyle
}

export type BadgeElement =
  | (BadgeElementBase & {
      type: BadgeElementType.TEXT
      data: BadgeTextData
    })
  | (BadgeElementBase & {
      type: BadgeElementType.FIELD
      data: BadgeFieldData
    })
  | (BadgeElementBase & {
      type: BadgeElementType.IMAGE
      data: BadgeImageData
    })
  | (BadgeElementBase & {
      type: BadgeElementType.LINE
      data: BadgeLineData
    })
  | (BadgeElementBase & {
      type: BadgeElementType.RECTANGLE
      data: BadgeShapeData
    })
  | (BadgeElementBase & {
      type: BadgeElementType.OVAL
      data: BadgeShapeData
    })
  | (BadgeElementBase & {
      type: BadgeElementType.QR_CODE
      data: BadgeQRData
    })

export enum BadgeTextSource {
  EVENT_NAME = "eventName",
  GUEST_FULL_NAME = "guestFullName",
  GUEST_FIRST_NAME = "guestFirstName",
  GUEST_LAST_NAME = "guestLastName",
  GUEST_COMPANY = "guestCompany",
  GUEST_JOB_TITLE = "guestJobTitle",
  GUEST_EMAIL = "guestEmail",
  GUEST_TABLE_ROW = "guestTableRow",
  GUEST_TABLE_ROW_SEAT = "guestTableRowSeat",
  GUEST_CITY_STATE = "guestCityState",
  TICKET_NAME = "ticketName",
  FORM_NAME = "formName",
  GUEST_LIST = "guestList",
}

export const BADGE_TEXT_SOURCE_LABELS: Record<BadgeTextSource, string> = {
  [BadgeTextSource.EVENT_NAME]: "Event Name",
  [BadgeTextSource.GUEST_FULL_NAME]: "Full Name",
  [BadgeTextSource.GUEST_FIRST_NAME]: "First Name",
  [BadgeTextSource.GUEST_LAST_NAME]: "Last Name",
  [BadgeTextSource.GUEST_COMPANY]: "Company",
  [BadgeTextSource.GUEST_JOB_TITLE]: "Title",
  [BadgeTextSource.GUEST_EMAIL]: "Email",
  [BadgeTextSource.GUEST_TABLE_ROW]: "Table/Row",
  [BadgeTextSource.GUEST_TABLE_ROW_SEAT]: "Table/Row & Seat",
  [BadgeTextSource.GUEST_CITY_STATE]: "City, State",
  [BadgeTextSource.TICKET_NAME]: "Ticket Name",
  [BadgeTextSource.FORM_NAME]: "Form Name",
  [BadgeTextSource.GUEST_LIST]: "Guest List",
}

export type BadgeTextData = {
  text: string
  font: string
  fontSize: number // unit: pt
  weight: BadgeFontWeight
  color: HexColor
  align: "left" | "center" | "right" | "justify"
  verticalAlign?: "top" | "center" | "bottom"
  bold: boolean
  italic: boolean
  textDecoration?: "none" | "underline" | "strikethrough"
  textTransform?: "none" | "capitalize" | "uppercase" | "lowercase"
}

export type BadgeFieldData = {
  source: BadgeTextSource
  font: string
  fontSize: number // pt
  weight: BadgeFontWeight
  color: HexColor
  align: "left" | "center" | "right" | "justify"
  verticalAlign?: "top" | "center" | "bottom"
  bold: boolean
  italic: boolean
  textDecoration?: "none" | "underline" | "strikethrough"
  textTransform?: "none" | "capitalize" | "uppercase" | "lowercase"
}

export type BadgeImageData = {
  url: string
  fit: "cover" | "contain" | "fill" // CSS object-fit value
  filters?: {
    blur?: number // px (0-20)
    brightness?: number // 0-2 (1 = normal)
    contrast?: number // 0-2 (1 = normal)
    grayscale?: number // 0-1 (0 = normal, 1 = full grayscale)
    hue?: number // degrees 0-360
    invert?: number // 0-1 (0 = normal, 1 = inverted)
    opacity?: number // 0-1 (1 = normal)
    saturate?: number // 0-2 (1 = normal)
    sepia?: number // 0-1 (0 = normal, 1 = full sepia)
  }
}

export type BadgeLineData = {
  color: HexColor
  thickness: number // mm
  style: "solid" | "dashed" | "dotted"
}

export type BadgeShapeData = {
  strokeColor: ColorValue
  fillColor: ColorValue
  thickness: number // mm
  style: "solid" | "dashed" | "dotted"
}

export enum BadgeQRFormat {
  GUEST_ID = "guest_id",
  VCARD = "vcard",
}

export const BADGE_QR_FORMAT_LABELS: Record<BadgeQRFormat, string> = {
  [BadgeQRFormat.GUEST_ID]: "Guest Check-in",
  [BadgeQRFormat.VCARD]: "Guest Check-in & vCard Exchange",
}

export type BadgeQRData = {
  format: BadgeQRFormat
}
